import { Routes } from '@angular/router';
import { authGuard } from './guards/auth-guard';
import { routeManagementGuard } from './guards/route-management-guard';
import { documentAccessGuard } from './guards/document-access-guard';
import { UserDataResolver } from './components/client-super-admin/employee-management/user-list/resolver/resolver';
import { ModuleGuard } from './guards/module-guard-guard';
import { nonProductionGuard } from './guards/non-production-guard';
import { documentEditorDeeplinkRedirectGuard } from './guards/document-editor-deeplink-redirect.guard';
import { pdfEditorUnsavedGuard } from './guards/pdf-editor-unsaved.guard';
import { licenseUtilizationConfigGuard } from './components/assets-mangement/sub-component/license-utilization-config/license-utilization-config.guard';
import {
  configurationIndexRedirect,
  configurationSectionGuard,
} from './components/assets-mangement/sub-component/configuration-shared/configuration-access';
import { myTeamModuleGuard } from './guards/my-team-module-guard';
import { billingModuleGuard } from './guards/billing-module-guard';
import { invoicePeerReviewGuard } from './guards/invoice-peer-review.guard';
import { invoiceManagerReviewGuard } from './guards/invoice-manager-review.guard';
import { supportProjectModuleGuard } from './guards/support-project-module-guard';
import { productivityModuleGuard } from './guards/productivity-module-guard';
import { timesheetReportGuard } from './guards/timesheet-report-guard';
import { manageTimesheetGuard } from './guards/manage-timesheet.guard';
import { timesheetApprovalGuard } from './guards/timesheet-approval.guard';
import { myTimesheetDeepLinkGuard } from './guards/my-timesheet-deep-link.guard';
import { rmTimesheetDeepLinkGuard } from './guards/rm-timesheet-deep-link.guard';
import {
  timesheetEntriesGuard,
  timesheetModuleGuard,
} from './guards/timesheet-module.guard';
import { pmInsightsGuard } from './guards/pm-insights-guard';
import { architectHomeGuard } from './guards/architect-home.guard';
import { resourceAllocationTabGuard } from './guards/resource-allocation-tab.guard';
import {
  crmLeadsGuard,
  crmOpportunitiesGuard,
} from './guards/crm-leads-opportunities.guard';
import {
  settingsEmployeeUpdateGuard,
  settingsModuleGuard,
  settingsOrgUpdateGuard,
  settingsRolesWriteGuard,
  settingsTabGuard,
} from './guards/settings-module.guard';
import {
  clientPortalDisabledCanMatch,
  clientPortalEnabledCanMatch,
} from './guards/client-portal-feature.guard';
import { environment } from '../environment/environment';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/redirection/redirection').then((m) => m.Redirection),
  },
  /** `/edit/:id` → `/dashboard/document-editing/edit/:id` (share links, editor backend defaults). */
  {
    path: 'edit/:id',
    canActivate: [documentEditorDeeplinkRedirectGuard],
    loadComponent: () =>
      import('./components/document-editing/document-share-viewer/document-share-viewer.component').then(
        (m) => m.DocumentShareViewerComponent,
      ),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./components/login/login').then((m) => m.Login),
  },
  {
    path: 'associate',
    canMatch: [clientPortalDisabledCanMatch],
    children: [{ path: '**', redirectTo: '/login' }],
  },
  {
    path: 'associate',
    canMatch: [clientPortalEnabledCanMatch],
    loadChildren: () =>
      import('./associate/associate.routes').then((m) => m.ASSOCIATE_ROUTES),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./components/oauth-callback/oauth-callback').then(
        (m) => m.OAuthCallbackComponent,
      ),
  },
  {
    path: 'help/forgot-password',
    loadComponent: () =>
      import('./components/forgot-password/forgot-password').then(
        (m) => m.ForgotPassword,
      ),
  },
  {
    path: 'help/forgot-password/verify-email',
    loadComponent: () =>
      import('./components/email-verification/email-verification').then(
        (m) => m.EmailVerification,
      ),
  },
  {
    path: 'help/reset-password',
    loadComponent: () =>
      import('./components/reset-password/reset-password').then(
        (m) => m.ResetPasswordComponent,
      ),
  },

  // External proposal review (no auth required)
  {
    path: 'proposal-review/:token',
    canActivate: [documentAccessGuard],
    loadComponent: () =>
      import('./components/external/proposal-review/proposal-review').then(
        (m) => m.ProposalReviewComponent
      ),
  },

  {
    path: 'proposals/viewer/:docId',
    canActivate: [documentAccessGuard],
    loadComponent: () =>
      import('./components/external/proposal-review/proposal-review').then(
        (m) => m.ProposalReviewComponent
      ),
  },

  /** Public DocuSign landing (nginx serves the SPA for /ds/*; Sign now creates a recipient view). */
  {
    path: 'ds/sign/:token',
    loadComponent: () =>
      import('./components/external/docusign-sign-landing/docusign-sign-landing.component').then(
        (m) => m.DocusignSignLandingComponent,
      ),
    data: { signerRole: 'client' },
  },
  {
    path: 'ds/pm-sign/:token',
    loadComponent: () =>
      import('./components/external/docusign-sign-landing/docusign-sign-landing.component').then(
        (m) => m.DocusignSignLandingComponent,
      ),
    data: { signerRole: 'pm' },
  },

  /** DocuSign embedded-signing return (no auth — session may be lost after redirect). */
  {
    path: 'docusign-completion/:documentId',
    loadComponent: () =>
      import('./components/external/docusign-completion/docusign-completion.component').then(
        (m) => m.DocusignCompletionComponent
      ),
  },

  {
    path: 'invoice-approval/:token',
    loadComponent: () =>
      import('./components/billing-&-invoice/utilities/invoice-client-approval/invoice-client-approval').then(
        (m) => m.InvoiceClientApprovalComponent,
      ),
  },
  {
    path: 'invoice/preview/test',
    loadComponent: () =>
      import('./components/external/invoice-preview-test/invoice-preview-test').then(
        (m) => m.InvoicePreviewTestPage,
      ),
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./components/layout/layout').then((m) => m.LayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },

      {
        path: 'invoice-peer-review/:invoiceId',
        canActivate: [routeManagementGuard, invoicePeerReviewGuard],
        loadComponent: () =>
          import(
            './components/billing-&-invoice/utilities/invoice-peer-review/invoice-peer-review.component'
          ).then((m) => m.InvoicePeerReviewComponent),
      },
      {
        path: 'invoice-manager-review/:invoiceId',
        canActivate: [routeManagementGuard, invoiceManagerReviewGuard],
        loadComponent: () =>
          import(
            './components/billing-&-invoice/utilities/invoice-manager-review/invoice-manager-review.component'
          ).then((m) => m.InvoiceManagerReviewComponent),
      },
      {
        path: 'crm-details/proposals',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import(
            './components/crm-component-tabs/crm-leads/show-lead/show-lead'
          ).then((m) => m.ShowLead),
      },
      {
        path: 'settings/announcements',
        canActivate: [routeManagementGuard, settingsModuleGuard],
        loadComponent: () =>
          import('./components/client-super-admin/client-super-admin').then(
            (m) => m.ClientSuperAdmin,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./features/announcements/announcements.component').then(
                (m) => m.AnnouncementsComponent,
              ),
          },
        ],
      },
      {
        path: 'settings',
        canActivate: [routeManagementGuard, settingsModuleGuard],
        loadComponent: () =>
          import('./components/client-super-admin/client-super-admin').then(
            (m) => m.ClientSuperAdmin,
          ),
        children: [
          {
            path: '',
            redirectTo: 'organization-details',
            pathMatch: 'full',
          },
          {
            path: 'organization-details',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/organization-details/select-organization-tab/select-organization-tab').then(
                (m) => m.SelectOrganizationTab,
              ),
          },
          {
            path: 'edit-organization-details',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/organization-details/edit-organiztion-details/edit-organiztion-details').then(
                (m) => m.EditOrganiztionDetails,
              ),
          },
          {
            path: 'edit-details',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/organization-details/edit-organization-details/edit-organization-details').then(
                (m) => m.EditOrganizationDetails,
              ),
          },
          {
            path: 'add-office-location',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/organization-details/edit-office-location/edit-office-location').then(
                (m) => m.EditOfficeLocation,
              ),
          },
          {
            path: 'edit-office-location/:id',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/organization-details/edit-office-location/edit-office-location').then(
                (m) => m.EditOfficeLocation,
              ),
          },
          {
            path: 'roles-&-permissions',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/roles-and-permision/roles-and-permision').then(
                (m) => m.RolesAndPermision,
              ),
          },
          {
            path: 'directory-master',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/directory-master/directory-master').then(
                (m) => m.DirectoryMaster,
              ),
          },
          {
            path: 'crm-configuration',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/select-configuration-category/select-configuration-category').then(
                (m) => m.SelectConfigurationCategory,
              ),
          },
          {
            path: 'announcements',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./features/announcements/announcements.component').then(
                (m) => m.AnnouncementsComponent,
              ),
          },
          {
            path: 'user-management',
            canActivate: [routeManagementGuard, settingsTabGuard],
            loadComponent: () =>
              import('./components/client-super-admin/employee-management/employee-management').then(
                (m) => m.EmployeeManagement,
              ),
          },
        ],
      },
      {
        path: 'edit-organization-details',
        canActivate: [routeManagementGuard, settingsOrgUpdateGuard],
        loadComponent: () =>
          import('./components/client-super-admin/organization-details/edit-organization-details/edit-organization-details').then(
            (m) => m.EditOrganizationDetails,
          ),
      },
      {
        path: 'edit-roles-details',
        canActivate: [routeManagementGuard, settingsRolesWriteGuard],
        loadComponent: () =>
          import('./components/client-super-admin/roles-and-permision/edit-roles-and-permission/edit-roles-and-permission').then(
            (m) => m.EditRolesAndPermission,
          ),
      },
      {
        path: 'user-info-add',
        canActivate: [routeManagementGuard, settingsEmployeeUpdateGuard],
        loadComponent: () =>
          import('./components/client-super-admin/employee-management/user-list/add-edit-user-info/add-edit-user-info').then(
            (m) => m.AddEditUserInfo,
          ),
      },
      {
        path: 'user-info-edit',
        canActivate: [routeManagementGuard, settingsEmployeeUpdateGuard],
        loadComponent: () =>
          import('./components/client-super-admin/employee-management/user-list/add-edit-user-info/add-edit-user-info').then(
            (m) => m.AddEditUserInfo,
          ),
      },
      {
        path: 'user-info',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/client-super-admin/employee-management/user-list/add-edit-user-info/add-edit-user-info').then(
            (m) => m.AddEditUserInfo,
          ),
      },
      {
        path: 'crm-details/leads',
        canActivate: [routeManagementGuard, ModuleGuard, crmLeadsGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-leads/show-lead/show-lead').then(
            (m) => m.ShowLead,
          ),
      },
      {
        path: 'crm-details/opportunity',
        canActivate: [routeManagementGuard, ModuleGuard, crmOpportunitiesGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-leads/show-lead/show-lead').then(
            (m) => m.ShowLead,
          ),
      },
      {
        path: 'crm-details/go-no-go',
        canActivate: [routeManagementGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-leads/show-lead/show-lead').then(
            (m) => m.ShowLead,
          ),
      },
      {
        path: 'crm-details/won-lost',
        canActivate: [routeManagementGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-leads/show-lead/show-lead').then(
            (m) => m.ShowLead,
          ),
      },
      {
        path: 'crm-details/client',
        canActivate: [routeManagementGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-leads/show-lead/show-lead').then(
            (m) => m.ShowLead,
          ),
      },
      {
        path: 'crm',
        canActivate: [routeManagementGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-component-tabs').then(
            (m) => m.CrmComponentTabs,
          ),
        children: [
          {
            path: '',
            redirectTo: 'crm-overview',
            pathMatch: 'full',
          },
          {
            path: 'crm-overview',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-overview/crm-overview').then(
                (m) => m.CrmOverview,
              ),
          },
          {
            path: 'crm-clients',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-clients/crm-clients').then(
                (m) => m.CrmClients,
              ),
          },
          {
            path: 'crm-contracts',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-contracts/crm-contracts').then(
                (m) => m.CrmContracts,
              ),
          },
          {
            path: 'crm-proposals',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-proposals/crm-proposals').then(
                (m) => m.CrmProposals,
              ),
          },

          {
            path: 'crm-go-no-go',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-go-no-go/crm-go-no-go').then(
                (m) => m.CrmGoNoGo,
              ),
          },
          {
            path: 'crm-leads',
            canActivate: [routeManagementGuard, crmLeadsGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-leads/crm-leads').then(
                (m) => m.CrmLeads,
              ),
          },
          {
            path: 'crm-marketing',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-marketing/crm-marketing').then(
                (m) => m.CrmMarketing,
              ),
          },
          {
            path: 'crm-opportunities',
            canActivate: [routeManagementGuard, crmOpportunitiesGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-opportunities/crm-opportunities').then(
                (m) => m.CrmOpportunities,
              ),
          },
          // {
          //   path: 'crm-overview',
          //    canActivate: [routeManagementGuard],
          //   loadComponent: () =>
          //     import(
          //       './components/crm-component-tabs/crm-overview/crm-overview'
          //     ).then((m) => m.CrmOverview),
          // },
          {
            path: 'crm-presentations',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-presentations/crm-presentations').then(
                (m) => m.CrmPresentations,
              ),
          },
          {
            path: 'crm-projects',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-projects/crm-projects').then(
                (m) => m.CrmProjects,
              ),
          },
          {
            path: 'project-directory',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-projects/updated-crm-projects/project-directory').then(
                (m) => m.ProjectDirectory,
              )
          },
          {
            path: 'crm-won-lost',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/crm-won-lost/crm-won-lost').then(
                (m) => m.CrmWonLost,
              ),
          },

        ],
      },


           {
            path: 'list-proposals',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/crm-component-tabs/list-proposals/list-proposals'
              ).then((m) => m.CrmProposalsComponent),

          },

          {
            path: 'document-share/:id',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/document-editing/document-share-viewer/document-share-viewer.component').then(
                (m) => m.DocumentShareViewerComponent,
              ),
          },
          {
            path: 'proposals/document-editor',
            canActivate: [routeManagementGuard, documentAccessGuard],
            loadComponent: () =>
              import('./components/document-generation/proposal-canvas/proposal-canvas.component'
                  ).then((m) => m.ProposalCanvasComponent),
          },
          {
            path: 'proposals/full-document-editor',
            canActivate: [routeManagementGuard, documentAccessGuard],
            loadComponent: () =>
              import('./components/document-generation/proposal-canvas/proposal-canvas.component'
                  ).then((m) => m.ProposalCanvasComponent),
          },
          {
            path: 'proposals/view-document',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/view-document/view-document'
                  ).then((m) => m.ViewDocumentComponent),
          },

          {
            path: 'proposals/document-viewer',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/document-generation/document-viewer/document-viewer'
                  ).then((m) => m.DocumentViewerComponent),
          },
          {
            path: 'proposals/asset-info',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/crm-component-tabs/list-proposals/view-document-asset/view-document-asset'
                  ).then((m) => m.ViewDocumentAssetComponent),
          },
      // {
      //   path: 'role-permissions',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import(
      //       './components/hiring-component/hiring-permissions/add-role-permissions/add-role-permissions'
      //     ).then((m) => m.AddRolePermissions),
      // },

      {
        path: 'client-management',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/client-super-admin/super-admin-client/super-admin-client').then(
            (m) => m.SuperAdminClient,
          ),
        children: [
          {
            path: '',
            redirectTo: 'client-details',
            pathMatch: 'full',
          },
          {
            path: 'client-details',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/client-super-admin/super-admin-client-management/super-admin-client-management').then(
                (m) => m.SuperAdminClientManagement,
              ),
          },
          {
            path: 'view-client-details/:id',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/client-super-admin/main-view-client-details/main-view-client-details').then(
                (m) => m.MainViewClientDetails,
              ),
          },
          {
            path: 'add-client-details',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/client-super-admin/main-add-client-details/main-add-client-details').then(
                (m) => m.MainAddClientDetails,
              ),
          },
          {
            path: 'edit-client-details/:id',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/client-super-admin/main-add-client-details/main-add-client-details').then(
                (m) => m.MainAddClientDetails,
              ),
          },
        ],
      },
      {
        path: 'hiring/evaluation-form',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/hiring-component/hiring-evaluation/create-evaluation-form/create-evaluation-form').then(
            (m) => m.CreateEvaluationForm,
          ),
      },
      {
        path: 'crm-analytics',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/crm-analytics/analytics/analytics').then(
            (m) => m.Analytics,
          ),
        children: [
          {
            path: '',
            redirectTo: 'sales-productivity',
            pathMatch: 'full',
          },
          {
            path: 'sales-productivity',
            loadComponent: () =>
              import('./components/crm-analytics/sales-productivity/sales-productivity').then(
                (m) => m.SalesProductivity,
              ),
          },
          {
            path: 'projects-lifecycle',
            loadComponent: () =>
              import('./components/crm-analytics/projects-lifecycle/projects-lifecycle').then(
                (m) => m.ProjectsLifecycle,
              ),
          },
          {
            path: 'opportunities-lifecycle',
            loadComponent: () =>
              import('./components/crm-analytics/opportunities-lifecycle/opportunities-lifecycle').then(
                (m) => m.OpportunitiesLifecycle,
              ),
          },
          {
            path: 'leads-lifecycle',
            loadComponent: () =>
              import('./components/crm-analytics/leads-lifecycle/leads-lifecycle').then(
                (m) => m.LeadsLifecycle,
              ),
          },
          {
            path: 'view-bd-productivity',
            loadComponent: () =>
              import('./components/crm-analytics/view-bd-productivity/view-bd-productivity').then(
                (m) => m.ViewBdProductivity,
              ),
            children: [
              {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full',
              },
              {
                path: 'overview',
                loadComponent: () =>
                  import('./components/crm-analytics/sales-productivity-overview/sales-productivity-overview').then(
                    (m) => m.SalesProductivityOverview,
                  ),
              },
              {
                path: 'conference',
                loadComponent: () =>
                  import('./components/crm-analytics/sales-productivity-conference/sales-productivity-conference').then(
                    (m) => m.SalesProductivityConference,
                  ),
              },
              {
                path: 'leads',
                loadComponent: () =>
                  import('./components/crm-analytics/sales-productivity-leads/sales-productivity-leads').then(
                    (m) => m.SalesProductivityLeads,
                  ),
              },
              {
                path: 'opportunities',
                loadComponent: () =>
                  import('./components/crm-analytics/sales-productivity-opportunities/sales-productivity-opportunities').then(
                    (m) => m.SalesProductivityOpportunities,
                  ),
              },
              {
                path: 'projects',
                loadComponent: () =>
                  import('./components/crm-analytics/sales-productivity-projects/sales-productivity-projects').then(
                    (m) => m.SalesProductivityProjects,
                  ),
              },
            ],
          },
        ],
      },

      // {
      //   path: 'hiring',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import('./components/hiring-component/hiring-component').then(
      //       (m) => m.HiringComponent
      //     ),
      //   children: [
      //     {
      //       path: '',
      //       redirectTo: 'overview',
      //       pathMatch: 'full',
      //     },
      //     {
      //       path: 'overview',
      //       loadComponent: () =>
      //         import(
      //           './components/hiring-component/hiring-overview-component/hiring-overview-component'
      //         ).then((m) => m.HiringOverviewComponent),
      //     },

      //     {
      //       path: 'hr-templates',
      //       loadComponent: () =>
      //         import(
      //           './components/hiring-component/hr-templates/hr-templates'
      //         ).then((m) => m.HrTemplates),
      //     },
      //     {
      //       path: 'jobs',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import('./components/organisation/job-posting/job-posting').then(
      //           (m) => m.JobPosting
      //         ),
      //     },
      //     {
      //       path: 'evaluation',
      //       loadComponent: () =>
      //         import(
      //           './components/hiring-component/hiring-evaluation/hiring-evaluation'
      //         ).then((m) => m.HiringEvaluation),
      //     },
      //     {
      //       path: 'candidate-pipeline',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/candidate-pipeline/candidate-pipeline'
      //         ).then((m) => m.CandidatePipeline),
      //     },
      //     {
      //       path: 'hiring-permissions',
      //       loadComponent: () =>
      //         import(
      //           './components/hiring-component/hiring-permissions/hiring-permissions'
      //         ).then((m) => m.HiringPermissions),
      //     },
      //   ],
      // },
      {
        path: 'inbox',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/inbox/inbox').then((m) => m.Inbox),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'approvals' },
          {
            path: 'threads',
            canMatch: [clientPortalDisabledCanMatch],
            children: [{ path: '**', redirectTo: '/dashboard/inbox/approvals' }],
          },
          {
            path: 'threads',
            canMatch: [clientPortalEnabledCanMatch],
            canActivate: [routeManagementGuard],
            data: { inboxTab: 'threads' },
            children: [],
          },
          {
            path: 'approvals',
            canActivate: [routeManagementGuard],
            data: { inboxTab: 'approvals' },
            children: [],
          },
          {
            path: 'issues',
            canActivate: [routeManagementGuard],
            data: { inboxTab: 'issues' },
            children: [],
          },
          {
            path: 'updates',
            canActivate: [routeManagementGuard],
            data: { inboxTab: 'updates' },
            children: [],
          },
          { path: '**', redirectTo: 'approvals' },
        ],
      },
      {
        path: 'home',
        canActivate: [routeManagementGuard, architectHomeGuard],
        loadComponent: () =>
          import('./components/home/home').then((m) => m.HomeComponent),
      },
      {
        path: 'document-editing',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/document-editing/document-editing').then(
            (m) => m.DocumentEditingComponent,
          ),
        children: [
          {
            // The standalone PDF editor library (PdfLibraryComponent) is no longer
            // user-facing — proposals are managed under /dashboard/list-proposals.
            // Anything landing on /dashboard/document-editing without a sub-route
            // (e.g. legacy bookmarks) bounces to the proposals list.
            path: '',
            pathMatch: 'full',
            redirectTo: '/dashboard/list-proposals',
          },
          {
            path: 'create',
            loadComponent: () =>
              import(
                './components/document-editing/pdf-editor/pdf-editor.component'
              ).then((m) => m.PdfEditorComponent),
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import(
                './components/document-editing/pdf-editor/pdf-editor.component'
              ).then((m) => m.PdfEditorComponent),
            canDeactivate: [pdfEditorUnsavedGuard],
          },
        ],
      },
      // {
      //   path: 'user-management',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import('./components/user-management/user-management').then(
      //       (m) => m.UserManagement
      //     ),
      // },
      // {
      //   path: 'hiring',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import('./components/organisation/hiring/hiring').then(
      //       (m) => m.HiringComponent
      //     ),
      // },

      // {
      //   path: 'jobs/post-a-new-job',
      //   canActivate: [routeManagementGuard, authGuard],
      //   loadComponent: () =>
      //     import(
      //       './components/organisation/job-posting/new-job-tabs/new-job-tabs'
      //     ).then((m) => m.NewJobTabs),
      //   children: [
      //     {
      //       path: '',
      //       redirectTo: 'job-basic-details',
      //       pathMatch: 'full',
      //     },
      //     {
      //       path: 'job-basic-details',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/job-posting/new-jobs/new-jobs'
      //         ).then((m) => m.CreateJobComponent),
      //     },
      //     {
      //       path: 'job-custom-stages',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/job-posting/new-job-tabs/job-custom-stages/job-custom-stages'
      //         ).then((m) => m.JobCustomStages),
      //     },
      //     {
      //       path: 'job-interviewers-forms',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/job-posting/new-job-tabs/job-interviewers-forms/job-interviewers-forms'
      //         ).then((m) => m.JobInterviewersForms),
      //     },
      //   ],
      // },

      // {
      //   path: 'template',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import('./components/organisation/template/template').then(
      //       (m) => m.TemplateComponent
      //     ),
      //   children: [
      //     {
      //       path: '',
      //       redirectTo: 'offer-letter',
      //       pathMatch: 'full',
      //     },

      //     {
      //       path: 'offer-letter',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/offer-letter/offer-letter'
      //         ).then((m) => m.OfferLetterComponent),
      //     },
      //     {
      //       path: 'email-template',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/email-template/email-template'
      //         ).then((m) => m.EmailTemplateComponent),
      //     },
      //   ],
      // },
      // {
      //   path: 'offer-email',
      //   loadComponent: () =>
      //     import('./components/organisation/offer-email/offer-email').then(
      //       (m) => m.OfferEmailComponent
      //     ),
      // },
      {
        path: 'profile',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/profile/route-edit-profile/route-edit-profile').then(
            (m) => m.RouteEditProfile,
          ),
      },
      {
        path: 'my-profile',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/profile/my-profile/my-profile').then(
            (m) => m.MyProfileComponent,
          ),
      },
      {
        path: 'profile/:employeeId', // manager viewing another employee
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/profile/my-profile/my-profile').then(
            (m) => m.MyProfileComponent,
          ),
      },
      { path: '', redirectTo: 'profile', pathMatch: 'full' },
      // {
      //   path: 'team-management',
      //   loadComponent: () =>
      //     import(
      //       './components/organisation/settings/team-management/team-management'
      //     ).then((m) => m.TeamManagement),
      //   children: [
      //     {
      //       path: '',
      //       redirectTo: 'team',
      //       pathMatch: 'full',
      //     },
      //     {
      //       path: 'team',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/settings/team-management/team/team'
      //         ).then((m) => m.Team),
      //     },
      //     {
      //       path: 'department',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/settings/team-management/department/department'
      //         ).then((m) => m.DepartmentComponent),
      //     },
      //     {
      //       path: 'designation',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/settings/team-management/designation/designation'
      //         ).then((m) => m.Designation),
      //     },
      //     {
      //       path: 'titles',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/organisation/settings/team-management/titles/titles'
      //         ).then((m) => m.Titles),
      //     },
      //   ],
      // },
      {
        path: 'roles-and-permissions',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/organisation/settings/role-permission/roles-permission-timeline/roles-permission-timeline').then(
            (m) => m.TimelineDrawerComponent,
          ),
      },

      // {
      //   path: 'help-support',
      //   loadComponent: () =>
      //     import('./components/help-support/help-support/help-support').then(
      //       (m) => m.HelpSupport
      //     ),
      //   children: [
      //     {
      //       path: 'faq',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import('./components/help-support/faq/faq').then(
      //           (m) => m.FaqComponent
      //         ),
      //     },
      //     {
      //       path: 'ticket',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import('./components/help-support/ticket/ticket').then(
      //           (m) => m.TicketComponent
      //         ),
      //     },
      //   ],
      // },
      // {
      //   path: 'documents/organization-file-list',
      //   loadComponent: () =>
      //     import(
      //       './components/documents/subcomponents/organization-files/sub-component/list-organization-file/list-organization-file'
      //     ).then((m) => m.ListOrganizationFile),
      //   canActivate: [authGuard, routeManagementGuard],
      // },
      // {
      //   path: 'documents',
      //   loadComponent: () =>
      //     import('./components/documents/documents').then((m) => m.Documents),
      //   canActivate: [authGuard],
      //   children: [
      //     {
      //       path: '',
      //       pathMatch: 'full',
      //       redirectTo: 'organization-files',
      //     },
      //     {
      //       path: 'organization-files',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/documents/subcomponents/organization-files/organization-files'
      //         ).then((m) => m.OrganizationFiles),
      //     },
      //     {
      //       path: 'employee-files',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/documents/subcomponents/employee-files/employee-files'
      //         ).then((m) => m.EmployeeFiles),
      //     },
      //   ],
      // },
      {
        path: 'my-project',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/project-management/all-projects/all-projects').then(
            (m) => m.AllProjects,
          ),
      },
      {
        path: 'support-projects',
        canActivate: [routeManagementGuard, supportProjectModuleGuard],
        loadComponent: () =>
          import('./components/support-projects/support-projects').then(
            (m) => m.SupportProjects,
          ),
      },
      /* --------------------MY TEAM ------------------------------------- */
      // {
      //   path: 'my-team',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import('./components/my-teams/my-teams').then((m) => m.MyTeams),
      // },
      {
        path: 'my-team',
        canActivate: [routeManagementGuard, myTeamModuleGuard],
        loadComponent: () =>
          import('./components/myTeam/my-team').then((m) => m.MyTeam),
      },
      {
        path: 'productivity',
        canActivate: [routeManagementGuard, productivityModuleGuard],
        loadComponent: () =>
          import('./components/productivity/productivity').then((m) => m.Productivity),
      },
      {
        path: 'support-projects/:id',
        canActivate: [routeManagementGuard, supportProjectModuleGuard],
        loadComponent: () =>
          import('./components/support-projects/support-project-detail/support-project-detail').then(
            (m) => m.SupportProjectDetail,
          ),
      },
      {
        path: 'projects/project-info',
        canActivate: [routeManagementGuard],
        loadComponent: () =>
          import('./components/crm-component-tabs/crm-projects/sub-componenets/project-info/project-info').then(
            (m) => m.ProjectInfo,
          ),
      },
      {
        path: 'my-assets',
        canActivate: [routeManagementGuard, authGuard],
        loadComponent: () =>
          import('./components/my-assets/my-assets').then((m) => m.MyAssets),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'assets-softwares',
          },
          {
            path: 'assets-softwares',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/my-assets/assets-and-softwares/assets-and-softwares').then(
                (m) => m.AssetsAndSoftwares,
              ),
          },
          {
            path: 'requests',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/my-assets/requests/requests').then(
                (m) => m.Requests,
              ),
          },
          {
            path: 'activity',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/my-assets/activity/activity').then(
                (m) => m.Activity,
              ),
          },
        ],
      },

      {
        path: 'view-employee-usage',
        canActivate: [routeManagementGuard, authGuard],
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/employee-usage/view-employee-usage/view-employee-usage').then(
            (m) => m.ViewEmployeeUsage,
          ),
      },
      {
        path: 'assets',
        canActivate: [routeManagementGuard, authGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/assets-mangement/assets-mangement').then(
            (m) => m.AssetsMangement,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'summary',
          },
          {
            path: 'configuration',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/assets-mangement/sub-component/configuration/configuration').then(
                (m) => m.Configuration,
              ),
          },
          {
            path: 'summary',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/assets-mangement/sub-component/summary/summary').then(
                (m) => m.Summary,
              ),
          },
          {
            path: 'assets-&-equipments',
            canActivate: [routeManagementGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./components/assets-mangement/sub-component/assets-and-equipment/assets-and-equipment').then(
                    (m) => m.AssetsAndEquipment,
                  ),
              },
              {
                path: 'asset-details/:slug',
                loadComponent: () =>
                  import('./components/assets-mangement/sub-component/assets-and-equipment/sub-component/assets-details-page/assets-details-page').then(
                    (m) => m.AssetsDetailsPage,
                  ),
              },
            ],
          },
          {
            path: 'software-license',
            canActivate: [routeManagementGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./components/assets-mangement/sub-component/software-and-license/software-and-license').then(
                    (m) => m.SoftwareAndLicense,
                  ),
              },
              {
                path: 'software-details/:slug',
                loadComponent: () =>
                  import('./components/assets-mangement/sub-component/software-and-license/subcomponents/software-details/software-details').then(
                    (m) => m.SoftwareDetails,
                  ),
              },
            ],
          },
          {
            path: 'license-utilization',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/assets-mangement/sub-component/license-utilization/license-utilization').then(
                (m) => m.LicenseUtilization,
              ),
          },
          {
            path: 'employee-usage',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/assets-mangement/sub-component/employee-usage/employee-usage').then(
                (m) => m.EmployeeUsage,
              ),
          },

          {
            path: 'requests',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/assets-mangement/sub-component/received-requests/received-requests').then(
                (m) => m.ReceivedRequests,
              ),
          },
          {
            path: 'asset-configuration',
            canActivate: [routeManagementGuard],
            children: [
              { path: '', pathMatch: 'full', redirectTo: configurationIndexRedirect },
              {
                path: 'categories',
                canActivate: [configurationSectionGuard],
                data: { section: 'categories' },
                loadComponent: () =>
                  import(
                    './components/assets-mangement/sub-component/configuration-categories/configuration-categories'
                  ).then((m) => m.ConfigurationCategories),
              },
              {
                path: 'tag-numbering',
                canActivate: [configurationSectionGuard],
                data: { section: 'tag-numbering' },
                loadComponent: () =>
                  import('./components/assets-mangement/sub-component/configuration/configuration').then(
                    (m) => m.Configuration,
                  ),
              },
              {
                path: 'vendors',
                canActivate: [configurationSectionGuard],
                data: { section: 'vendors' },
                loadComponent: () =>
                  import('./components/assets-mangement/sub-component/vendor-master/vendor-master').then(
                    (m) => m.VendorMaster,
                  ),
              },
              {
                path: 'usage-policies',
                canActivate: [configurationSectionGuard],
                canDeactivate: [licenseUtilizationConfigGuard],
                data: { section: 'usage-policies' },
                loadComponent: () =>
                  import(
                    './components/assets-mangement/sub-component/license-utilization-config/license-utilization-config'
                  ).then((m) => m.LicenseUtilizationConfig),
              },
              { path: 'license-utilization', redirectTo: 'usage-policies' },
            ],
          },
        ],
      },
      // [
      //     "Computing Devices",
      //     "Servers & Storage",
      //     "Networking Equipments",
      //     "Accessories & Peripherals",
      //     "Others"
      // ]
      {
        path: 'assets/software-license/communication-&-collaboration',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/software-and-license/subcomponents/software-details/software-details').then(
            (m) => m.SoftwareDetails,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/software-license/business-&-operations',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/software-and-license/subcomponents/software-details/software-details').then(
            (m) => m.SoftwareDetails,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/software-license/creative-&-technical-tools',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/software-and-license/subcomponents/software-details/software-details').then(
            (m) => m.SoftwareDetails,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/software-license/security-&-compliance',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/software-and-license/subcomponents/software-details/software-details').then(
            (m) => m.SoftwareDetails,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/software-license/others',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/software-and-license/subcomponents/software-details/software-details').then(
            (m) => m.SoftwareDetails,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'finance',
        canActivate: [routeManagementGuard, authGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/finance-comp/finance-comp').then(
            (m) => m.FinanceComp,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'projects',
          },
          {
            path: 'projects',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/finance-comp/sub-component/projects/projects').then(
                (m) => m.Projects,
              ),
          },
          {
            path: 'forecast',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/finance-comp/sub-component/forecast/forecast').then(
                (m) => m.Forecast,
              ),
          },
        ],
      },
      {
        path: 'billing-&-invoice',
        canActivate: [routeManagementGuard, authGuard, billingModuleGuard],
        loadComponent: () =>
          import('./components/billing-&-invoice/billing-&-invoice').then(
            (m) => m.BillingInvoiceComponent,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'overview',
          },
          {
            path: 'overview',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/billing-&-invoice/sub-components/overview/overview').then(
                (m) => m.BillingOverviewComponent,
              ),
          },
          {
            path: 'projects',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/billing-&-invoice/sub-components/projects/projects').then(
                (m) => m.BillingProjectsComponent,
              ),
          },
          {
            path: 'projects/:projectId',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/billing-&-invoice/sub-components/projects/project-detail/project-detail'
              ).then((m) => m.BillingProjectDetailComponent),
          },
          {
            path: 'clients',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/billing-&-invoice/sub-components/clients/clients').then(
                (m) => m.BillingClientsComponent,
              ),
          },
          {
            path: 'clients/:clientId/billing-rates',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/billing-&-invoice/sub-components/clients/client-detail/billing-rates/billing-rates'
              ).then((m) => m.BillingClientRatesComponent),
          },
          {
            path: 'clients/:clientId',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/billing-&-invoice/sub-components/clients/client-detail/client-detail'
              ).then((m) => m.BillingClientDetailComponent),
          },
          {
            path: 'billing',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/billing-&-invoice/sub-components/billing/billing').then(
                (m) => m.BillingInvoicesComponent,
              ),
          },
          {
            path: 'upcoming',
            pathMatch: 'full',
            redirectTo: 'upcomming',
          },
          {
            path: 'upcomming',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/billing-&-invoice/sub-components/upcoming/upcoming').then(
                (m) => m.BillingUpcomingComponent,
              ),
          },
          {
            path: 'billing/:invoiceId',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/billing-&-invoice/sub-components/billing/invoice-detail/invoice-detail'
              ).then((m) => m.BillingInvoiceDetailComponent),
          },
        ],
      },
      // -----------------------------------------------------------------------------------------------------------------------------------------------------
      {
        path: 'budget',
        canActivate: [routeManagementGuard, authGuard, ModuleGuard],
        loadComponent: () =>
          import('./components/budget/budget').then((m) => m.Budget),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'budget-details',
          },
          {
            path: 'overview',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/budget/sub-component/overview/overview').then(
                (m) => m.Overview,
              ),
          },
          {
            path: 'budget-details',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/budget/sub-component/budget-details/budget-details').then(
                (m) => m.BudgetDetails,
              ),
          },
          {
            path: 'configuration',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/budget/sub-component/configuration/configuration').then(
                (m) => m.Configuration,
              ),
          },
        ],
      },
      {
        path: 'expenses',
        canActivate: [routeManagementGuard, authGuard],
        loadComponent: () =>
          import('./components/expennses/expennses').then((m) => m.Expennses),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'overview',
          },
          {
            path: 'overview',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/expennses/sub-components/overview/overview').then(
                (m) => m.Overview,
              ),
          },
          {
            path: 'expense-submission',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/expennses/sub-components/expense-submission/expense-submission').then(
                (m) => m.ExpenseSubmission,
              ),
          },
          {
            path: 'budget',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/expennses/sub-components/budget/budget').then(
                (m) => m.Budget,
              ),
          },
        ],
      },
      {
        path: 'expenses/review-budget',
        loadComponent: () =>
          import('./components/expennses/sub-components/review-budget-details/review-budget-details').then(
            (m) => m.ReviewBudgetDetails,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      // -----------------------------------------------------------------------------------------------------------------------------------------------------
      {
        path: 'assets/assets-&-equipments/computing-devices',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/assets-and-equipment/sub-component/assets-details-page/assets-details-page').then(
            (m) => m.AssetsDetailsPage,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/assets-&-equipments/servers-&-storage',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/assets-and-equipment/sub-component/assets-details-page/assets-details-page').then(
            (m) => m.AssetsDetailsPage,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/assets-&-equipments/networking-equipments',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/assets-and-equipment/sub-component/assets-details-page/assets-details-page').then(
            (m) => m.AssetsDetailsPage,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/assets-&-equipments/accessories-&-peripherals',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/assets-and-equipment/sub-component/assets-details-page/assets-details-page').then(
            (m) => m.AssetsDetailsPage,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      {
        path: 'assets/assets-&-equipments/others',
        loadComponent: () =>
          import('./components/assets-mangement/sub-component/assets-and-equipment/sub-component/assets-details-page/assets-details-page').then(
            (m) => m.AssetsDetailsPage,
          ),
        canActivate: [authGuard, routeManagementGuard],
      },
      // {
      //   path: 'timesheet-final',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import(
      //       './components/timesheet-final/timesheet.component'
      //     ).then((m) => m.TimesheetComponent),
      // },

      {
        path: 'timesheet',
        canActivate: [routeManagementGuard, timesheetModuleGuard],
        loadComponent: () =>
          import('./components/timesheet-mangement/timesheet-mangement').then(
            (m) => m.TimesheetMangement,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'my-timesheet',
          },
          {
            path: 'my-timesheet',
            canActivate: [routeManagementGuard, timesheetEntriesGuard, myTimesheetDeepLinkGuard],
            loadComponent: () =>
              import('./components/timesheet-final/timesheet.component').then((m) => m.TimesheetComponent),
          },
          {
            path: 'approval-timsheet',
            canActivate: [routeManagementGuard, timesheetEntriesGuard, rmTimesheetDeepLinkGuard],
            loadComponent: () =>
              import(
                './components/timesheet-final/rm-timesheet.component'
              ).then((m) => m.RmTimesheetComponent),
          },
          {
            path: 'report',
            canActivate: [routeManagementGuard, timesheetReportGuard],
            loadComponent: () =>
              import(
                './components/timesheet-mangement/timesheet-report/timesheet-report'
              ).then((m) => m.TimesheetReport),
          },
          {
            path: 'manage-timesheet',
            canActivate: [routeManagementGuard, manageTimesheetGuard],
            loadComponent: () =>
              import('./components/manage-timesheet/manage-timesheet.component').then(
                (m) => m.ManageTimesheetComponent,
              ),
          },
          {
            path: 'timesheet-approval',
            canActivate: [routeManagementGuard, timesheetApprovalGuard],
            loadComponent: () =>
              import('./components/timesheet-approval/timesheet-approval.component').then(
                (m) => m.TimesheetApprovalComponent,
              ),
          },
          {
            path: 'timesheet-final',
            pathMatch: 'full',
            redirectTo: 'my-timesheet',
          },
          {
            path: 'pm-timesheet',
            canActivate: [routeManagementGuard, timesheetEntriesGuard],
            loadComponent: () =>
              import(
                './components/PM_timesheet_view/pm-timesheet-view.component'
              ).then((m) => m.PmTimesheetViewComponent),
          },
        ],
      },
      // {
      //   path: 'ppt',
      //   canActivate: [routeManagementGuard],
      //   loadComponent: () =>
      //     import('./components/ppt-editor/ppt-editor').then((m) => m.PptEditor),
      // },
      {
        path: 'project-management',
        loadComponent: () =>
          import('./components/project-management/project-management').then(
            (m) => m.ProjectManagement,
          ),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'summary',
          },
          {
            path: 'summary',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/project-management/sub-component/summary/summary').then(
                (m) => m.Summary,
              ),
          },
          {
            path: 'allocation',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import('./components/project-management/sub-component/timeline/timeline').then(
                (m) => m.Timeline,
              ),
          },
          {
            path: 'work-plan',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/work-plan/work-plan'
              ).then((m) => m.WorkPlan),
          },
          {
            path: 'directory',
            canActivate: [routeManagementGuard, resourceAllocationTabGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/poject-details/poject-details'
              ).then((m) => m.ProjectDetails),
          },
          {
            path: 'documents',
            canActivate: [routeManagementGuard, resourceAllocationTabGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/directory/directory'
              ).then((m) => m.Documents),
          },
          /** Deep-link / email alias for documents (`?projectId=&folderId=`). */
          {
            path: 'files',
            canActivate: [routeManagementGuard, resourceAllocationTabGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/directory/directory'
              ).then((m) => m.Documents),
          },
          {
            path: 'project-setup',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/project-setup/project-setup'
              ).then((m) => m.ProjectSetup),
          },
          {
            path: 'project-timesheet',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/PM_timesheet_view/pm-timesheet-view.component'
              ).then((m) => m.PmTimesheetViewComponent),
          },
          // {
          //   path: 'project-timesheet',
          //   canActivate: [routeManagementGuard],
          //   loadComponent: () =>
          //     import('./components/project-management/sub-component/poject-timesheet/poject-timesheet').then(
          //       (m) => m.PojectTimesheet,
          //     ),
          // },
          // {
          //   path: 'project-timesheet',
          //   canActivate: [routeManagementGuard],
          //   loadComponent: () =>
          //     import(
          //       './components/project-management/sub-component/project-timesheet-approval/project-timesheet-approval'
          //     ).then((m) => m.ProjectTimesheetApproval),
          // },
          {
            path: 'project-team',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/project-teams/project-teams'
              ).then((m) => m.ProjectTeams),
          },
          {
            path: 'feedback',
            canActivate: [routeManagementGuard, resourceAllocationTabGuard],
            loadComponent: () =>
              import('./components/project-management/sub-component/poject-feedback/poject-feedback').then(
                (m) => m.PojectFeedback,
              ),
          },
          {
            path: 'discussion',
            canMatch: [clientPortalDisabledCanMatch],
            children: [{ path: '**', redirectTo: '/dashboard/project-management/summary' }],
          },
          {
            path: 'discussion',
            canMatch: [clientPortalEnabledCanMatch],
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/project-discussions/project-discussions'
              ).then((m) => m.ProjectDiscussions),
          },
          {
            path: 'project-notes',
            canActivate: [routeManagementGuard, resourceAllocationTabGuard],
            loadComponent: () =>
              import('./components/project-management/sub-component/poject-notes/poject-notes').then(
                (m) => m.PojectNotes,
              ),
          },
          // Insights route
          {
            path: 'insights',
            canActivate: [routeManagementGuard, pmInsightsGuard, resourceAllocationTabGuard],
            loadComponent: ()=>import('./components/project-management/sub-component/insights/insights').then(
              (m) => m.Insights,
            ),
          },
          {
            path: 'project-score',
            canActivate: [routeManagementGuard],
            loadComponent: () =>
              import(
                './components/project-management/sub-component/project-score/project-score.component',
              ).then((m) => m.ProjectScoreComponent),
          },
        ],
      },
      // {
      //   path: 'onboarding',
      //   loadComponent: () =>
      //     import('./components/onboarding/onboarding').then(
      //       (m) => m.Onboarding
      //     ),
      //   canActivate: [authGuard],
      //   children: [
      //     {
      //       path: '',
      //       pathMatch: 'full',
      //       redirectTo: 'candidates',
      //     },
      //     {
      //       path: 'candidates',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/onboarding/components/candidate/candidate'
      //         ).then((m) => m.Candidate),
      //     },
      //     {
      //       path: 'flows',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import('./components/onboarding/components/flows/flows').then(
      //           (m) => m.Flows
      //         ),
      //     },
      //     {
      //       path: 'configuration',
      //       canActivate: [routeManagementGuard],
      //       loadComponent: () =>
      //         import(
      //           './components/onboarding/components/configuration/configuration'
      //         ).then((m) => m.Configuration),
      //     },
      //   ],
      // },
      // {
      //   path: 'onboarding/add-candidate',
      //   loadComponent: () =>
      //     import('./components/onboarding/add-candidate/add-candidate').then(
      //       (m) => m.AddCandidate
      //     ),
      //   canActivate: [authGuard, routeManagementGuard],
      // },
      // {
      //   path: 'onboarding/edit-candidate',
      //   loadComponent: () =>
      //     import('./components/onboarding/add-candidate/add-candidate').then(
      //       (m) => m.AddCandidate
      //     ),
      //   canActivate: [authGuard, routeManagementGuard],
      // },
      // {
      //   path: 'onboarding/view-candidate',
      //   loadComponent: () =>
      //     import('./components/onboarding/view-candidate/view-candidate').then(
      //       (m) => m.ViewCandidate
      //     ),
      //   canActivate: [authGuard, routeManagementGuard],
      // },
      // {
      //   path: 'onboarding/edit-flow',
      //   loadComponent: () =>
      //     import(
      //       './components/onboarding/components/flows/edit-flow/edit-flow'
      //     ).then((m) => m.EditFlow),
      //   canActivate: [authGuard, routeManagementGuard],
      // },
    ],
  },
  // {
  //   path: 'view-job/:jobCode',
  //   canActivate: [routeManagementGuard],
  //   loadComponent: () =>
  //     import('./components/organisation/job-posting/view-job/view-job').then(
  //       (m) => m.ViewJob
  //     ),
  // },
  // {
  //   path: 'upload-documents',
  //   loadComponent: () =>
  //     import(
  //       './components/organisation/candidate-pipeline/candidate-details-dialog/request-for-documents-public-page/request-for-documents-public-page'
  //     ).then((m) => m.RequestForDocumentsPublicPage),
  // },
  // {
  //   path: 'thank-you-message',
  //   loadComponent: () =>
  //     import('./components/thank-you-component/thank-you-component').then(
  //       (m) => m.ThankYouComponent
  //     ),
  // },
  // {
  //   path: 'candidate-onboarding-form/:candidateCode',
  //   canActivate: [routeManagementGuard],
  //   loadComponent: () =>
  //     import(
  //       './components/candidate-onboarding-flow/candidate-onboarding-flow'
  //     ).then((m) => m.CandidateOnboardingFlow),
  // },
  {
    path: '**',
    loadComponent: () =>
      import('./components/page-not-found/page-not-found').then(
        (m) => m.PageNotFound,
      ),
  },
];
